// ABOUTME: Billing settings page showing current subscription and usage.
// ABOUTME: Allows managing subscription via Stripe portal and upgrading for free users.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, TrendingUp, AlertCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { billingApi } from '@/services/api/billingClient';
import { formatTierName, formatBillingDate, formatPreciseDate } from '@/utils/billing';
import { useApp } from '@/context/AppContext';
import type { UsageSummary } from '@/types/api';

interface UsageMeterProps {
  label: string;
  used: number;
  limit: number;
}

function UsageMeter({ label, used, limit }: UsageMeterProps) {
  const isUnlimited = limit === -1;
  const percentage = isUnlimited ? 0 : (used / limit) * 100;
  const getColor = () => {
    if (isUnlimited) return 'bg-primary-500';
    if (percentage >= 90) return 'bg-error';
    if (percentage >= 70) return 'bg-warning';
    return 'bg-primary-500';
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-surface-600">{label}</span>
        <span className="font-medium text-surface-900">
          {used} / {isUnlimited ? 'Unlimited' : limit}
        </span>
      </div>
      <div className="h-2 bg-surface-200 rounded-full overflow-hidden">
        {!isUnlimited && (
          <div
            className={`h-full rounded-full transition-all ${getColor()}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        )}
      </div>
    </div>
  );
}

export function BillingPage() {
  const navigate = useNavigate();
  const { isSelfService } = useApp();
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadUsage = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await billingApi.getUsage();
      setUsage(data);
    } catch (err) {
      console.error('Failed to load usage:', err);
      setError('Unable to load billing information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const handleManageSubscription = async () => {
    setIsPortalLoading(true);
    setSuccessMessage(null);
    try {
      const returnUrl = window.location.href;
      const { portal_url } = await billingApi.createBillingPortalSession(returnUrl);
      window.location.href = portal_url;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
      setError('Unable to open billing portal. Please try again.');
      setIsPortalLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/upgrade');
  };

  const handleReactivate = async () => {
    setIsReactivating(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await billingApi.reactivateSubscription();
      setSuccessMessage('Your subscription has been reactivated successfully.');
      await loadUsage();
    } catch (err) {
      console.error('Failed to reactivate subscription:', err);
      setError(
        'Unable to reactivate subscription. Please try using the Manage Subscription button to access the Stripe portal.'
      );
    } finally {
      setIsReactivating(false);
    }
  };

  const isFreeTier = usage?.subscription_tier === 'freemium';

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3 text-surface-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading billing information...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !usage) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <AlertCircle className="w-12 h-12 text-error" />
          <p className="text-surface-600">{error}</p>
          <Button onClick={loadUsage}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-surface-900 mb-2">
          Billing & Subscription
        </h1>
        <p className="text-surface-600">
          Manage your subscription and view your usage.
        </p>
      </div>

      {error && (
        <div role="alert" className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
          {error}
        </div>
      )}

      {successMessage && (
        <div role="alert" className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
          {successMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Plan Card */}
        <Card variant="glass">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-surface-900">
                Current Plan
              </h2>
              <p className="text-surface-600">
                {usage && formatTierName(usage.subscription_tier)}
              </p>
            </div>
          </div>

          {usage && (
            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-surface-500">Billing Period</span>
                <span className="text-surface-900">
                  {formatBillingDate(usage.billing_period_start)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-surface-500">Resets In</span>
                <span className="text-surface-900">
                  {usage.days_until_reset} days
                </span>
              </div>
            </div>
          )}

          {usage?.cancel_at_period_end && usage.subscription_period_end && (
            <div role="status" className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-800">
                    Your {formatTierName(usage.subscription_tier)} subscription is
                    scheduled to cancel on{' '}
                    <span className="font-semibold">
                      {formatPreciseDate(usage.subscription_period_end)}
                    </span>
                    . You&apos;ll be downgraded to the Free plan after this date.
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    className="mt-3"
                    onClick={handleReactivate}
                    isLoading={isReactivating}
                  >
                    Reactivate Subscription
                  </Button>
                </div>
              </div>
            </div>
          )}

          {isSelfService && isFreeTier && (
            <Button
              variant="primary"
              className="w-full"
              onClick={handleUpgrade}
            >
              <TrendingUp className="w-4 h-4" />
              Upgrade to Pro
            </Button>
          )}
          {isSelfService && !isFreeTier && (
            <Button
              variant="secondary"
              className="w-full"
              onClick={handleManageSubscription}
              isLoading={isPortalLoading}
            >
              Manage Subscription
            </Button>
          )}
        </Card>

        {/* Usage Card */}
        <Card variant="glass">
          <h2 className="text-xl font-semibold text-surface-900 mb-6">
            Current Usage
          </h2>

          {usage && (
            <div className="space-y-6">
              <UsageMeter
                label="Messages"
                used={usage.usage.messages.used}
                limit={usage.usage.messages.limit}
              />
              <UsageMeter
                label="Active Investigations"
                used={usage.usage.active_investigations.used}
                limit={usage.usage.active_investigations.limit}
              />
              <UsageMeter
                label="Data Sources"
                used={usage.usage.data_sources.used}
                limit={usage.usage.data_sources.limit}
              />
              <UsageMeter
                label="Reports"
                used={usage.usage.reports_generated.used}
                limit={usage.usage.reports_generated.limit}
              />
            </div>
          )}
        </Card>
      </div>

      {/* Upgrade Banner for Free Self-Service Users */}
      {isSelfService && isFreeTier && (
        <Card variant="surface" className="mt-6 bg-gradient-to-r from-primary-50 to-primary-100 border-primary-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-surface-900">
                Need more power?
              </h3>
              <p className="text-surface-600">
                Upgrade to Base Individual for 75 messages/month, 20 investigations, and more.
              </p>
            </div>
            <Button variant="primary" onClick={handleUpgrade}>
              View Plans
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
